import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Minus, 
  Trash2, 
  ShoppingCart, 
  AlertTriangle, 
  Package,
  Building2,
  FileText,
  Truck,
  Scan
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { cartService } from '../../services/cartService';
import { productService } from '../../services/productService';
import { chantierService } from '../../services/chantierService';
import { CartItem, Product, Chantier } from '../../types';

interface CartModalProps {
  onClose: () => void;
  onCartUpdate: () => void;
}

function CartModal({ onClose, onCartUpdate }: CartModalProps) {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [selectedChantier, setSelectedChantier] = useState('');
  const [mode, setMode] = useState<'preparation' | 'sortie_immediate'>('preparation');
  const [loading, setLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [itemComments, setItemComments] = useState<Record<string, string>>({});

  useEffect(() => {
    loadCartData();
  }, []);

  const loadCartData = async () => {
    try {
      const cart = cartService.getCart();
      setCartItems(cart);
      
      if (cart.length > 0) {
        // Charger les détails des produits
        const productPromises = cart.map(item => productService.getProduct(item.productId));
        const productResults = await Promise.all(productPromises);
        setProducts(productResults.filter(p => p !== null) as Product[]);
      }
      
      // Charger les chantiers disponibles
      const chantiersData = await chantierService.getChantiers({ status: 'en_cours' });
      setChantiers(chantiersData);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de charger les données du panier',
        type: 'error'
      });
    }
  };

  const handleBarcodeAdd = async () => {
    if (!barcodeInput.trim()) return;
    
    try {
      // Rechercher le produit par code-barres
      const allProducts = await productService.getProducts({ search: barcodeInput });
      const product = allProducts.find(p => p.barcode === barcodeInput);
      
      if (product) {
        cartService.addToCart(product.id, 1);
        setBarcodeInput('');
        await loadCartData();
        onCartUpdate();
        
        addNotification({
          userId: user?.id || '',
          title: 'Produit ajouté',
          message: `${product.designation} ajouté au panier`,
          type: 'success'
        });
      } else {
        addNotification({
          userId: user?.id || '',
          title: 'Produit non trouvé',
          message: 'Aucun produit trouvé avec ce code-barres',
          type: 'error'
        });
      }
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Erreur lors de la recherche du produit',
        type: 'error'
      });
    }
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      cartService.removeFromCart(productId);
    } else {
      cartService.updateCartItem(productId, newQuantity);
    }
    loadCartData();
    onCartUpdate();
  };

  const removeItem = (productId: string) => {
    cartService.removeFromCart(productId);
    loadCartData();
    onCartUpdate();
  };

  const getProduct = (productId: string) => {
    return products.find(p => p.id === productId);
  };

  const checkStockAvailability = (productId: string, quantity: number) => {
    const product = getProduct(productId);
    if (!product) return { available: false, stock: 0 };
    
    const availableStock = productService.getAvailableStock(product);
    return {
      available: availableStock >= quantity,
      stock: availableStock,
      suggestions: availableStock < quantity ? getSuggestions(product) : []
    };
  };

  const getSuggestions = (product: Product) => {
    // Simuler des suggestions d'alternatives
    return products
      .filter(p => p.categoryId === product.categoryId && p.id !== product.id)
      .slice(0, 3);
  };

  const handleValidateCart = async () => {
    if (!selectedChantier) {
      addNotification({
        userId: user?.id || '',
        title: 'Chantier requis',
        message: 'Veuillez sélectionner un chantier avant de valider',
        type: 'error'
      });
      return;
    }

    if (cartItems.length === 0) {
      addNotification({
        userId: user?.id || '',
        title: 'Panier vide',
        message: 'Ajoutez des produits avant de valider',
        type: 'error'
      });
      return;
    }

    try {
      setLoading(true);

      // Vérifier la disponibilité des stocks pour sortie immédiate
      if (mode === 'sortie_immediate') {
        const stockIssues = cartItems.filter(item => {
          const check = checkStockAvailability(item.productId, item.quantity);
          return !check.available;
        });

        if (stockIssues.length > 0) {
          addNotification({
            userId: user?.id || '',
            title: 'Stock insuffisant',
            message: `${stockIssues.length} produit(s) n'ont pas assez de stock pour une sortie immédiate`,
            type: 'error'
          });
          return;
        }
      }

      // Créer la commande chantier
      const orderItems = cartItems.map(item => {
        const product = getProduct(item.productId);
        return {
          id: `item-${Date.now()}-${item.productId}`,
          productId: item.productId,
          quantity: item.quantity,
          preparedQuantity: mode === 'sortie_immediate' ? item.quantity : 0,
          unitPrice: 0, // Prix interne, à définir selon la logique métier
          totalPrice: 0,
          comment: itemComments[item.productId] || ''
        };
      });

      const newOrder = await chantierService.createChantierOrder({
        chantierId: selectedChantier,
        status: mode === 'preparation' ? 'a_preparer' : 'sortie_validee',
        items: orderItems,
        totalAmount: 0, // Calculé selon la logique métier
        createdBy: user?.id || '',
        notes: `Commande créée depuis le panier - Mode: ${mode === 'preparation' ? 'Préparation' : 'Sortie immédiate'}`
      });

      // Si sortie immédiate, générer les mouvements de stock
      if (mode === 'sortie_immediate') {
        for (const item of cartItems) {
          await productService.adjustStock(
            item.productId,
            -item.quantity,
            `Sortie immédiate chantier ${selectedChantier}`,
            user?.id || ''
          );
        }
      }

      // Vider le panier
      cartService.clearCart();
      onCartUpdate();

      addNotification({
        userId: user?.id || '',
        title: mode === 'preparation' ? 'Demande de préparation créée' : 'Sortie immédiate validée',
        message: `Commande #${newOrder.id.slice(-6)} créée avec succès`,
        type: 'success'
      });

      onClose();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de valider le panier',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="modal-overlay">
      <div className="modal-content p-5 border w-full max-w-4xl shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <ShoppingCart className="h-6 w-6 text-orange-600" />
            <h3 className="text-xl font-semibold text-gray-900">
              Panier ({totalItems} article{totalItems > 1 ? 's' : ''})
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Scan code-barres */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <Scan className="h-5 w-5 text-gray-600" />
            <div className="flex-1">
              <input
                type="text"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value.replace(/\D/g, ''))}
                onKeyPress={(e) => e.key === 'Enter' && handleBarcodeAdd()}
                placeholder="Scanner ou saisir un code-barres (chiffres uniquement)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <button
              onClick={handleBarcodeAdd}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
            >
              Ajouter
            </button>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Panier vide</h3>
            <p className="mt-1 text-sm text-gray-500">
              Ajoutez des produits depuis les listes ou scannez un code-barres
            </p>
          </div>
        ) : (
          <>
            {/* Articles du panier */}
            <div className="max-h-96 overflow-y-auto mb-6">
              <div className="space-y-3">
                {cartItems.map((item) => {
                  const product = getProduct(item.productId);
                  const stockCheck = checkStockAvailability(item.productId, item.quantity);
                  
                  if (!product) return null;

                  return (
                    <div key={item.productId} className="flex items-center space-x-4 p-4 bg-white border border-gray-200 rounded-lg">
                      {product.image && (
                        <img
                          src={product.image}
                          alt={product.designation}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {product.designation}
                        </h4>
                        <p className="text-xs text-gray-500">
                          Réf: {product.reference}
                        </p>
                        <p className="text-xs text-gray-500">
                          Stock disponible: {stockCheck.stock}
                        </p>
                        
                        {!stockCheck.available && (
                          <div className="mt-2">
                            <div className="flex items-center text-xs text-red-600">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Stock insuffisant
                            </div>
                            {stockCheck.suggestions && stockCheck.suggestions.length > 0 && (
                              <div className="mt-1">
                                <p className="text-xs text-gray-600">Alternatives :</p>
                                {stockCheck.suggestions.slice(0, 2).map(alt => (
                                  <button
                                    key={alt.id}
                                    onClick={() => {
                                      cartService.removeFromCart(item.productId);
                                      cartService.addToCart(alt.id, item.quantity);
                                      loadCartData();
                                      onCartUpdate();
                                    }}
                                    className="block text-xs text-blue-600 hover:text-blue-800 truncate"
                                  >
                                    → {alt.designation}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Commentaire */}
                        <input
                          type="text"
                          value={itemComments[item.productId] || ''}
                          onChange={(e) => setItemComments(prev => ({
                            ...prev,
                            [item.productId]: e.target.value
                          }))}
                          placeholder="Commentaire (optionnel)"
                          className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateQuantity(item.productId, parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                        />
                        
                        <button
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Configuration de la commande */}
            <div className="border-t pt-6 space-y-4">
              {/* Sélection du chantier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chantier de destination *
                </label>
                <select
                  value={selectedChantier}
                  onChange={(e) => setSelectedChantier(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                >
                  <option value="">Sélectionner un chantier</option>
                  {chantiers.map(chantier => (
                    <option key={chantier.id} value={chantier.id}>
                      {chantier.code} - {chantier.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mode de traitement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mode de traitement
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMode('preparation')}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      mode === 'preparation'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <FileText className="h-5 w-5 mr-2" />
                      <span className="font-medium">Demande de préparation</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Crée une commande "À préparer" pour le dépôt
                    </p>
                  </button>
                  
                  <button
                    onClick={() => setMode('sortie_immediate')}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      mode === 'sortie_immediate'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center mb-2">
                      <Truck className="h-5 w-5 mr-2" />
                      <span className="font-medium">Sortie immédiate</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Génère immédiatement les mouvements de sortie
                    </p>
                  </button>
                </div>
              </div>

              {/* Vérification des stocks pour sortie immédiate */}
              {mode === 'sortie_immediate' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Vérification des stocks
                  </h4>
                  {cartItems.map(item => {
                    const product = getProduct(item.productId);
                    const stockCheck = checkStockAvailability(item.productId, item.quantity);
                    
                    return (
                      <div key={item.productId} className="flex items-center justify-between text-xs">
                        <span className="text-gray-700 truncate">
                          {product?.designation}
                        </span>
                        <span className={`font-medium ${
                          stockCheck.available ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {item.quantity}/{stockCheck.stock}
                          {stockCheck.available ? ' ✓' : ' ✗'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4">
                <button
                  onClick={() => {
                    cartService.clearCart();
                    onCartUpdate();
                    onClose();
                  }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Vider le panier
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Fermer
                  </button>
                  
                  <button
                    onClick={handleValidateCart}
                    disabled={loading || !selectedChantier || cartItems.length === 0}
                    className="px-6 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        {mode === 'preparation' ? <FileText className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                        <span>
                          {mode === 'preparation' ? 'Créer demande' : 'Sortie immédiate'}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CartModal;